<script setup lang="ts">
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useQueryClient } from '@tanstack/vue-query';
import AppHeader from './components/AppHeader.vue';
import SiteFooter from './components/SiteFooter.vue';
import ConsentBanner from './components/ConsentBanner.vue';
import { askForConsent } from './lib/analytics-consent';
import { configurationProblem } from './lib/session';
import { ApiError } from './lib/api';
import { footerFrom as pageFooterFrom } from './lib/palette';

const route = useRoute();
const router = useRouter();
const problem = configurationProblem();
// The page's own ground: a themed project's background, or the route's.
const ground = computed(() => pageFooterFrom.value ?? route.meta.footerFrom ?? null);
const footerFrom = computed(() => ground.value ?? 'var(--brand-bg)');

/**
 * 402 anywhere sends the person to the paywall. The API decides entitlement
 * (§3.1); the SPA only reacts to its `code`, never to a status phrase.
 */
const client = useQueryClient();
client.getQueryCache().subscribe((event) => {
  const error = event.query.state.error;
  // Public routes (a project's landing page) handle "not a member" themselves.
  if (
    error instanceof ApiError &&
    error.code === 'subscription-required' &&
    route.path !== '/' &&
    !route.meta.public
  ) {
    void router.replace('/');
  }
});
</script>

<template>
  <div class="flex min-h-screen flex-col">
    <AppHeader />
    <p v-if="problem" role="alert" class="bg-rose-600 px-4 py-2 text-sm text-white">
      {{ $t('errors.config', { problem }) }}
    </p>
    <!-- A page with its own ground (the landing page's black) fills down to
         the footer, rather than leaving a band of the theme background. -->
    <div class="flex-1" :style="ground ? { background: ground } : undefined">
      <RouterView />
    </div>
    <SiteFooter :from-color="footerFrom" />
    <ConsentBanner v-if="askForConsent" />
  </div>
</template>
